<template>
  <pdl-collapse-item
    v-if="hasSpecData"
    :qaid="`${componentPrefix}-${componentData.specItemHeader}`"
    :title="componentData.specItemHeader"
  >
    <dl class="font-body flex flex-row flex-wrap text-gray-80 text-lg" :aria-label="$t('product.details.specs.title')">
      <template v-if="specItemList">
        <template v-for="(specs, specType) in groupedSpecs" :key="`dt-${specType}`">
          <dt
            class="flex-none w-full md:w-1/5 mb-0 py-2 pr-1 font-medium border-b-2 md:border-r-2 border-gray-10"
            :qaid="`${componentPrefix}-${specType}-0`"
          >
            {{ specs.length > 1 ? `*${specs[0].localizedSpecItemType}` : specs[0].localizedSpecItemType }}
          </dt>

          <dd class="flex-none w-full md:w-4/5 font-normal">
            <template v-for="(spec, specIndex) in specs" :key="`spec-${specType}-${specIndex}`">
              <template v-if="hasSizesData(spec)">
                <div
                  v-for="(entry, entryIndex) in spec.marketingDescWithFrameSizesMap"
                  :key="`entry-${entryIndex}`"
                  class="p-2 border-b-2 border-gray-10"
                  :qaid="`${componentPrefix}-size-${entryIndex}`"
                >
                  <template v-if="specs.length > 1">
                    <span>{{ $t('product.variants.size') }}:</span>
                    <span> {{ entry.value.join(', ') }}<br /> </span>
                  </template>
                  <template v-if="spec.equivalentSKU">
                    <a
                      :href="`${encodedContextPath}/p/${spec.equivalentSKU}`"
                      :qaid="`${componentPrefix}-size-link-${entryIndex}`"
                      class="block underline"
                      v-html="entry.key"
                    />
                  </template>
                  <template v-else>
                    <div :qaid="`${componentPrefix}-size-nonlink-${entryIndex}`" v-html="entry.key" />
                  </template>
                </div>
              </template>
              <template v-else>
                <div class="p-2 border-b-2 border-gray-10" v-html="spec.marketingDescription" />
                >
              </template>
            </template>
          </dd>
        </template>
      </template>

      <template v-if="coreAttributesList">
        <template v-for="attributeItem in coreAttributesList" :key="attributeItem.label">
          <dt
            class="flex-none w-full md:w-1/5 mb-0 py-2 pr-1 font-medium border-b-2 md:border-r-2 border-gray-10"
            :qaid="`${componentPrefix}-core-item-${attributeItem.label}`"
            v-html="attributeItem.label"
          />
          <dd
            class="flex-none w-full md:w-4/5 p-2 font-normal border-b-2 border-gray-10"
            v-html="attributeItem.value"
          />
        </template>
      </template>
    </dl>
  </pdl-collapse-item>
</template>

<script>
import {mapState} from 'vuex';
import {PdlCollapseItem} from '@pedal/pdl-collapse';

export default {
  components: {
    PdlCollapseItem,
  },
  props: {
    componentData: {
      type: Object,
      required: true,
    },
    productSpecs: {
      type: Object,
      default: () => null,
    },
    product: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      componentPrefix: 'product-spec-bom',
    };
  },
  computed: {
    ...mapState('backend', ['encodedContextPath']),

    hasSpecData() {
      return !!this.specItemList?.length || !!this.coreAttributesList?.length;
    },
    specItems() {
      if (!this.componentData?.specItems || !Object.keys(this.componentData?.specItems)?.length) return;
      return this.componentData?.specItems?.split(',');
    },
    specItemList() {
      if (!this.productSpecs?.specs) return;

      //Filters out unsed types not found in specItems
      const filtered = this.productSpecs?.specs.filter((item) => this.specItems?.find((type) => type === item.type));

      return filtered;
    },
    groupedSpecs() {
      if (!this.specItemList?.length) return;
      let obj = {};
      this.specItems?.forEach((item) => {
        obj[item] = [];
        this.specItemList.forEach((filteredItem) => {
          if (filteredItem.type === item) {
            obj[item].push(filteredItem);
          }
        });
      });

      // eslint-disable-next-line no-unused-vars
      const usedSpecs = Object.entries(obj).filter(([key, value]) => value?.length);
      const arrayToObject = Object.fromEntries(usedSpecs);

      return arrayToObject;
    },
    coreAttributesList() {
      if (!this.componentData?.attributes) return;
      const attributes = this.componentData?.attributes?.split(',');
      const coreList = attributes
        ?.map((item) => {
          return {
            label: this.$t(`product.specification.${item}`),
            value: this.product?.specs[item],
          };
        })
        .filter((item) => item?.value);
      return coreList;
    },
  },
  methods: {
    hasSizesData(spec) {
      return !!spec?.marketingDescWithFrameSizesMap?.length;
    },
  },
};
</script>
